import React from "react";
import { MessageSquareReply, MessageSquare } from "lucide-react";
import { Card, Button } from "@/components/ui";
import { ReviewSentiment } from "@/services/bedrock";

interface ReviewTextReplyCardProps {
  platformName: string;
  isInternalFeedback: boolean;
  onOpenDialog: () => void;
  sourceSystem?: string;
  sentiment: ReviewSentiment;
}

const textReplyConfigs: Record<ReviewSentiment, { badges: string[] }> = {
  positive: {
    badges: ["Win Future Clients", "Public Social Proof"],
  },
  neutral: {
    badges: ["Boost Visibility", "Shows Responsiveness"],
  },
  negative: {
    badges: ["Protect Reputation", "Build Public Trust"],
  },
};

export function ReviewTextReplyCard({
  platformName,
  isInternalFeedback,
  onOpenDialog,
  sourceSystem,
  sentiment,
}: ReviewTextReplyCardProps) {
  if (isInternalFeedback) return null;

  const isDirectPostingSupported = sourceSystem === "GL" || sourceSystem === "FB";
  const config = textReplyConfigs[sentiment] || textReplyConfigs.neutral;

  return (
    <Card
      onClick={onOpenDialog}
      className="flex flex-col pt-10 pb-6 px-6 md:pt-12 md:pb-8 md:px-8 border-2 border-primary/70 bg-linear-to-br from-indigo-50/50 via-white to-white rounded-2xl relative shadow-sm cursor-pointer hover:shadow-md transition-all duration-300"
    >
      {/* Full-width header indicator */}
      <div className="absolute top-0 left-0 right-0 text-primary text-xs font-extrabold py-1.5 border-b-2 border-primary/70 rounded-t-2xl uppercase tracking-widest flex items-center justify-center gap-1 shrink-0 select-none bg-indigo-50 z-10">
        Highly Recommended
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl border border-indigo-200 bg-indigo-100 text-primary shrink-0 shadow-inner hidden sm:flex">
            <MessageSquareReply className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-1">
            <div className="font-bold text-lg text-slate-900 tracking-tight">Reply on {platformName}</div>

            {/* Benefit Badges */}
            <div className="flex flex-wrap gap-2">
              {config.badges.map((badge, index) => (
                <div
                  key={index}
                  className="bg-primary/5 text-primary border border-primary/10 px-2 py-0.5 rounded-md text-[12px] font-bold uppercase tracking-wider"
                >
                  {badge}
                </div>
              ))}
            </div>

            <div className="text-sm text-slate-500 font-medium mt-3">
              Only takes <span className="font-bold text-slate-700">a few seconds</span>. Just review the suggested
              reply.
            </div>
          </div>
        </div>

        <Button
          variant="default"
          onClick={e => {
            // Prevent double click firing when container is also clickable
            e.stopPropagation();
            onOpenDialog();
          }}
          className="w-full md:w-auto h-10 font-bold flex items-center justify-center gap-2 shrink-0 px-6"
        >
          {isDirectPostingSupported ? (
            <>
              <MessageSquare className="h-4 w-4" />
              Reply Directly
            </>
          ) : (
            <>
              <MessageSquareReply className="h-4 w-4" />
              Reply with Text
            </>
          )}
        </Button>
      </div>
    </Card>
  );
}
